import React, { useContext } from 'react';
import { compact, find, isEmpty } from 'lodash';
import { routes } from 'constants/config';
import { FilterContext } from 'components/shared/FilterContext/index';
import brain from 'images/brain.svg';
import Count from '../_shared/Count';
import DateRangeFormat from '../_shared/DateRangeFormat';
import InsightsTable from '../_shared/InsightsTable';
import DataCardHeader from '../_shared/DataCardHeader';
import DataCardTitle from '../_shared/DataCard/Title';
import ActiveUsersRow from './ActiveUsersRow';
import MonthlyActiveUsersChart from './MonthlyActiveUsersChart';
import TabSection from '../_shared/TabSection';
import TabSectionTab from '../_shared/TabSectionTab';

const hasDateFilter = filters => filters.start_date || filters.end_date;
const hasGroupsFilters = filters => !isEmpty(filters.group_ids);

const getTotalCountTooltip = filters => {
  const hasGroups = hasGroupsFilters(filters);
  const hasDate = hasDateFilter(filters);
  return find([
    hasDate &&
      hasGroups &&
      t('insights.active_users.total_count.description_with_all_filters'),
    hasDate &&
      !hasGroups &&
      t('insights.active_users.total_count.description_with_date_filter'),
    !hasDate &&
      hasGroups &&
      t('insights.active_users.total_count.description_with_groups_filter'),
    t('insights.active_users.total_count.description'),
  ]);
};

const getMonthlyCountsTooltip = filters => {
  const hasGroups = hasGroupsFilters(filters);
  const hasDate = hasDateFilter(filters);
  return find([
    hasDate &&
      hasGroups &&
      t('insights.active_users.monthly_counts.description_with_all_filters'),
    hasDate &&
      !hasGroups &&
      t('insights.active_users.monthly_counts.description_with_date_filter'),
    !hasDate &&
      hasGroups &&
      t('insights.active_users.monthly_counts.description_with_groups_filter'),
    t('insights.active_users.monthly_counts.description'),
  ]);
};

const ActiveUsersModule = ({ i18n }) => {
  const { params: filterParams } = useContext(FilterContext);

  const withFilterQuery = url =>
    compact([url, $.param(filterParams)]).join(url.includes('?') ? '&' : '?');

  const topAccessingTooltip = hasDateFilter(filterParams)
    ? t('insights.active_users.top_accessing.description_with_filter')
    : t('insights.active_users.top_accessing.description');

  const last90DaysSubtitle = hasDateFilter(filterParams) ? (
    <DateRangeFormat
      start={filterParams.start_date}
      end={filterParams.end_date}
    />
  ) : (
    t('insights.timeframes.last_90_days')
  );

  const last6MonthsSubtitle = hasDateFilter(filterParams) ? (
    <DateRangeFormat
      start={filterParams.start_date}
      end={filterParams.end_date}
    />
  ) : (
    t('insights.timeframes.last_6_months')
  );

  return (
    <section className="DataCard mb-24">
      <DataCardHeader
        title={i18n.active_users.total_count.title}
        tooltip={getTotalCountTooltip(filterParams)}
        subtitle={last90DaysSubtitle}
        downloadLink={withFilterQuery(routes.INSIGHTS.ACTIVE_USERS.REPORT)}
        reportTitle={i18n.active_users.report.title}
      >
        <Count
          route={routes.INSIGHTS.ACTIVE_USERS.COUNT}
          imgSrc={brain}
          imgDescription={`${i18n.active_users.total_count.title}:`}
        />
      </DataCardHeader>
      <div className="DataCard-main">
        <DataCardTitle
          title={i18n.active_users.monthly_counts.title}
          tooltip={getMonthlyCountsTooltip(filterParams)}
          subtitle={last6MonthsSubtitle}
        />
        <MonthlyActiveUsersChart i18n={i18n} />
        <TabSection
          title={i18n.active_users.top_accessing.title}
          subtitle={last90DaysSubtitle}
          tooltip={topAccessingTooltip}
          className="mt-32"
        >
          <TabSectionTab title={t('people.title')}>
            <InsightsTable
              i18n={i18n}
              source={withFilterQuery(
                routes.INSIGHTS.ACTIVE_USERS.MOST_ACTIVE_USERS,
              )}
              filteredSource={withFilterQuery(
                routes.INSIGHTS.ACTIVE_USERS.MOST_ACTIVE_NON_ADMIN,
              )}
              rowComponent={ActiveUsersRow}
              filterableBy="Admins"
            />
          </TabSectionTab>
          <TabSectionTab title={i18n.tabs.groups}>
            <InsightsTable
              i18n={i18n}
              source={withFilterQuery(
                routes.INSIGHTS.ACTIVE_USERS.MOST_ACTIVE_GROUPS,
              )}
              rowComponent={ActiveUsersRow}
            />
          </TabSectionTab>
        </TabSection>
      </div>
    </section>
  );
};

export default ActiveUsersModule;
